import { shopWindowTypes, valueNames } from "@/modules/Constants";
import { icons } from "@/modules/icons";
import ShopWindowConfig, { openingSlideType } from "@/plugin/ShopWindowConfig";
import LinkData, { linkTypes } from "@/plugin/LinkData";
import * as labelUtils from "@/plugin/labelUtils";
import config from "@/modules/config";
import { orientationType } from "@/modules/orientationManager";
import { experienceTypes, clusterTypes } from "@/modules/ExperienceData";
import ShopWindowConfigList from "@/plugin/ShopWindowConfigList";
import type { Orientation } from "@/modules/orientationManager";
import type { ExperienceState, PluginEvent } from "@/types/state";

export const shopWindowNames = {
  PRODUCT: "product",
};

export const clusterNames = {
  ROOT: "root",
};

export default class ProductPlugin {
  static SHOP_WINDOW_TEMPLATES = [
    new ShopWindowConfig(
      shopWindowNames.PRODUCT,
      shopWindowTypes.PRODUCT,
      "Products",
      clusterNames.ROOT,
      icons.CART,
      false,
      {
        isFavorites: true,
        openingSlideType: openingSlideType.HINTS,
        showHintTap: true,
      },
    ),
  ];

  declare shopWindowMap: Record<string, ShopWindowConfig>;

  init(state: ExperienceState) {
    const applicationData = state.applicationData;
    const experienceData = state.experienceData;

    this.shopWindowMap = {};

    for (
      let n = 0, max = ProductPlugin.SHOP_WINDOW_TEMPLATES.length;
      n < max;
      n++
    ) {
      const template = ProductPlugin.SHOP_WINDOW_TEMPLATES[n];
      const config = ShopWindowConfig.fromTemplate(template);
      this.shopWindowMap[config.name] = config.init(experienceData);
    }

    this._registerShopWindows(state);

    applicationData.setValue(valueNames.HAS_CONTROL_BAR, true);

    this.shopWindowMap[shopWindowNames.PRODUCT].links = {
      cta: new LinkData(
        linkTypes.ARTICLE,
        undefined,
        "product.cta",
        true,
        labelUtils.getClusterLabel(clusterNames.ROOT, experienceData.style),
      ),
    };

    this._onOrientation(
      state,
      state.applicationData.getValue(valueNames.ORIENTATION) as Orientation,
    );
  }

  _registerShopWindows(state: ExperienceState) {
    const experienceData = state.experienceData;
    const applicationData = state.applicationData;
    const pluginWindows = ProductPlugin.SHOP_WINDOW_TEMPLATES;

    const configs = new ShopWindowConfigList();
    let defaultWindow: ShopWindowConfig | null = null;
    let videoTimelineName: string | null = null;

    for (let n = 0, max = pluginWindows.length; n < max; n++) {
      const pluginWindow = pluginWindows[n];
      const config = this.shopWindowMap[pluginWindow.name];
      const clusterType = experienceData.getClusterType(config.clusterName);

      if (clusterType !== clusterTypes.NONE) {
        if (clusterType === clusterTypes.TIMELINE) {
          if (
            defaultWindow == null &&
            pluginWindow.isPortraitOverlay === false
          ) {
            defaultWindow = config;
          }
          if (videoTimelineName == null) {
            videoTimelineName = config.clusterName;
          }
        }
        configs.add(config);
      }
    }

    applicationData.setValue(valueNames.SHOP_WINDOW_CONFIG_LIST, configs);

    if (defaultWindow) {
      applicationData.setValue(
        valueNames.SHOP_WINDOW_DEFAULT_CONFIG,
        defaultWindow,
      );
      applicationData.setValue(valueNames.SHOP_WINDOW_CONFIG, defaultWindow);
    }

    if (videoTimelineName) {
      applicationData.setValue(
        valueNames.VIDEO_TIMELINE_NAME,
        videoTimelineName,
      );
    }

    const data = this.shopWindowMap[shopWindowNames.PRODUCT].data;
    data.isFavorites = experienceData.type === experienceTypes.FAVORITES;
    data.showHintTap = experienceData.styleFlags.showPanelHintTap();
    data.openingSlideType = experienceData.styleFlags.showPanelHintsView()
      ? openingSlideType.HINTS
      : openingSlideType.NONE;
  }

  getShopWindowConfig(name: string): ShopWindowConfig | undefined {
    return this.shopWindowMap[name];
  }

  handleEvent(state: ExperienceState, e: PluginEvent): boolean {
    return false;
  }

  willSetValue(state: ExperienceState, key: string, value: unknown) {
    if (key === valueNames.ORIENTATION) {
      this._onOrientation(state, value as Orientation);
    }
  }

  _onOrientation(state: ExperienceState, orientation: Orientation) {
    // For products, if there is no data for the control bar in portrait mode, don't show it.
    const applicationData = state.applicationData;
    // Only a missing shop window config counts here.
    const shopWindowConfig = applicationData.getValue(
      valueNames.SHOP_WINDOW_CONFIG,
    );
    const hasControlBar =
      orientation === orientationType.LANDSCAPE ||
      (shopWindowConfig === undefined && config.shareLink());
    applicationData.setValue(valueNames.HAS_CONTROL_BAR, hasControlBar);
  }
}
